import React, { useRef } from 'react';
import { Label } from '../ui/label';
import { Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import { MAX_FILE_SIZE_MB, MAX_FILES } from './feedbackConstants';

/**
 * Screenshot upload + preview grid + paste/select support.
 */
export default function ScreenshotUploader({ screenshots, setScreenshots }) {
  const fileInputRef = useRef(null);

  const addFile = (file) => {
    if (screenshots.length >= MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} screenshots allowed`);
      return;
    }
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(
        `Screenshot must be less than ${MAX_FILE_SIZE_MB}MB. Your file: ${fileSizeMB}MB`
      );
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshots((prev) => [
        ...prev,
        { file, preview: reader.result, uploadedUrl: null, uploading: false },
      ]);
    };
    reader.readAsDataURL(file);
  };

  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) addFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAt = (index) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div>
      <Label>
        Screenshots{' '}
        <span className="text-gray-400 text-xs">(optional, max {MAX_FILES})</span>
      </Label>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        onChange={handleSelect}
        className="hidden"
      />

      {screenshots.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {screenshots.map((ss, index) => (
            <div
              key={`screenshot-${index}-${ss.file?.name || 'uploaded'}`}
              className="relative w-20 h-20"
            >
              <img
                src={ss.preview}
                alt={`Screenshot ${index + 1}`}
                className="w-full h-full object-cover rounded-lg border bg-gray-50"
              />
              <button
                onClick={() => removeAt(index)}
                className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {screenshots.length < MAX_FILES && (
        <button
          onClick={() => fileInputRef.current?.click()}
          className="mt-2 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition-colors flex items-center justify-center gap-2 text-gray-500 hover:text-amber-600"
        >
          <Camera className="h-4 w-4" />
          <span className="text-sm">
            {screenshots.length === 0 ? 'Add Screenshot' : 'Add Another'}
          </span>
        </button>
      )}

      <p className="text-xs text-gray-400 mt-1">
        Max {MAX_FILE_SIZE_MB}MB each. Tip: Paste with Ctrl/Cmd+V
      </p>
    </div>
  );
}
